import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import {
  Typography,
  ClientPaymentList,
  ConfigureCardModal,
  Button,
  Icon,
  ConfirmationModal,
  SplitButton,
} from 'components'
import { useDispatch, useSelector } from 'react-redux'
import { CONSTANTS, colors } from 'constants/index'
import { getClientPaymentSecret, saveUserCard, getUserCards, getBankAccounts } from 'store/actions/payments'
import { navigateToClientInvoices } from 'store/actions/navigation'
import AddBankAccount from '../../../components/clientAccount/payments/ACHform'

const useStyles = makeStyles({
  payWithContainer: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    padding: 40,
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
  },
  btnContainer: {
    marginTop: 16,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  btnSubmit: {
    marginLeft: 15,
  },
  buttonIcon: {
    marginRight: 4,
    fontSize: 15,
  },
  manageButton: {
    height: 40,
    minHeight: 32,
    fontSize: 11,
    marginLeft: 12,
  },
  manageInnerButtons: {
    height: 40,
    minHeight: 32,
    fontSize: 11,
    padding: '5px 10px',
  },
  manageIconButton: {
    minWidth: 'auto',
    padding: '5px 10px 5px 0',
    borderLeft: 'none',
    '&:hover': {
      borderLeft: 'none',
    },
  },
})

const ClientPayWithContainer = ({ paymentMethod, setPaymentMethod, onSubmit, disablePayment }) => {
  const classes = useStyles()
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const clientPaymentSecretKey = useSelector(state => state.payments.clientPaymentSecretKey)
  const clientSecretIsLoading = useSelector(state => state.payments.clientSecretIsLoading)
  const userCards = useSelector(state => state.payments.userCards)
  const bankAccounts = useSelector(state => state.payments.bankAccounts)
  const [userCardData, setUserCardData] = useState({})
  const [isModalShown, setIsModalShown] = useState(false)
  const [isCancelModalShown, setIsCancelModalShown] = useState(false)
  const [isBankModalShown, setIsBankModalShown] = useState(false)
  const [selectedBankType, setSelectedBankType] = useState(null)
  const [stripeError, setStripeError] = useState('')

  useEffect(() => {
    if (userCardData.payment_method_id) {
      const newCard = userCards.find(card => userCardData.payment_method_id === card.paymentMethodId)
      if (newCard) setPaymentMethod(newCard)
    }
  }, [userCards, userCardData, setPaymentMethod])

  useEffect(() => {
    dispatch(getBankAccounts())
  }, [dispatch])

  const onAddPayment = () => {
    dispatch(getClientPaymentSecret())
    setIsModalShown(true)
  }

  const onAddBankAccount = bankType => {
    setSelectedBankType(bankType)
    setIsBankModalShown(true)
  }

  const onBankModalClose = () => {
    setIsBankModalShown(false)
    setSelectedBankType(null)
  }

  const handleSubmit = async () => {
    if (!userCards.length && !bankAccounts.length) {
      // No payment methods available, show card form
      if (!stripe || !elements) {
        return
      }
      const result = await stripe.confirmCardSetup(clientPaymentSecretKey, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      })

      if (result.error) {
        setStripeError(result.error.message)
      } else {
        setStripeError('')
        const data = {
          label: userCardData.label,
          is_default: userCardData.isDefault,
          remember_card: true,
          payment_method_id: result.setupIntent.payment_method,
        }
        try {
          await dispatch(saveUserCard(data))
          dispatch(getUserCards())
          onSubmit(data.payment_method_id)
        } catch (e) {
          dispatch(getClientPaymentSecret())
        }
      }
    } else if (paymentMethod) {
      const paymentMethodId = paymentMethod.paymentMethodId || paymentMethod.payment_method_id
      if (paymentMethodId) {
        onSubmit(paymentMethodId)
      }
    }
  }

  const onModalSubmit = async data => {
    try {
      const { payment_method_id } = await dispatch(saveUserCard(data))
      await dispatch(getUserCards(payment_method_id))
      setUserCardData(data)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      setIsModalShown(false)
    }
  }
  return (
    <div className={classes.payWithContainer}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography className={classes.title}>Pay with</Typography>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button variant="secondary" size="small" onClick={onAddPayment} className={classes.manageButton}>
            <Icon style={classes.buttonIcon} /> add card
          </Button>
          <SplitButton
            variant="secondary"
            color="secondary"
            size="small"
            onSelect={bankType => onAddBankAccount(bankType)}
            options={['US bank account', 'Canadian bank account']}
            classes={{
              buttonGroup: classes.manageButton,
              buttons: classes.manageInnerButtons,
              iconButton: classes.manageIconButton,
            }}
          >
            <Icon style={classes.buttonIcon} /> add bank account
          </SplitButton>
        </div>
      </div>
      <ClientPaymentList
        userCards={userCards}
        bankAccounts={bankAccounts}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        userCardData={userCardData}
        setUserCardData={setUserCardData}
        stripeError={stripeError}
        hideStripeForm={true}
      />
      <div className={classes.btnContainer} style={{ marginTop: !userCards.length && !userCardData.length ? 400 : 16 }}>
        <div style={{ justifyContent: 'flex-end' }}>
          <Button variant="text" onClick={() => setIsCancelModalShown(true)}>
            cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className={classes.btnSubmit}
            variant="primary"
            disabled={disablePayment || !(!!userCards.length || !!bankAccounts.length)}
          >
            confirm payment
          </Button>
        </div>
      </div>
      <ConfigureCardModal
        isOpen={isModalShown}
        onClose={() => setIsModalShown(false)}
        onSave={onModalSubmit}
        mode={CONSTANTS.MODE.ADD}
        clientPaymentSecretKey={clientPaymentSecretKey}
        clientSecretIsLoading={clientSecretIsLoading}
        cardData={{ label: '', isDefault: false, remember: true }}
        showIsRememberOption={userCards.length}
      />
      <ConfirmationModal
        isOpen={isCancelModalShown}
        message={`If you cancel the process the invoice won't be paid.`}
        onClose={() => setIsCancelModalShown(false)}
        title="Cancel payment?"
        onConfirm={() => dispatch(navigateToClientInvoices())}
        confirmationButtonText="yes"
        cancelationButtonText="no"
      />
      <AddBankAccount isOpen={isBankModalShown} onClose={onBankModalClose} bankType={selectedBankType} />
    </div>
  )
}
export default ClientPayWithContainer
